import makeOrder from '../services/makeOrder';
import splitArray from './splitArray';

const useBatchAPICalls = (setCountPendingOrders) => {
  // Helper: extrae los atributos de un item
  const extractAttributes = (item) => {
    if (!item) return null;
    return item?.attributes ?? item;
  };

  // Helper: procesa una respuesta y extrae todos los resultados posibles
  const processResponseData = (responseData) => {
    if (!responseData) return [];

    // Si es un array, procesar cada elemento
    if (Array.isArray(responseData)) {
      return responseData.map(extractAttributes).filter(Boolean);
    }

    // Si tiene data anidado
    if (responseData.data) {
      if (Array.isArray(responseData.data)) {
        return responseData.data.map(extractAttributes).filter(Boolean);
      }
      return [extractAttributes(responseData.data)].filter(Boolean);
    }

    // Si tiene attributes directamente
    if (responseData.attributes) {
      return [responseData.attributes];
    }

    // Objeto directo válido
    return [responseData];
  };

  const batchAPICalls = async ({
    pendingOrders,
    batchSize = 1,
    onBatchComplete = null
  }) => {
    const batches = splitArray(pendingOrders, batchSize);
    const results = [];
    let currentIndex = 0;

    // eslint-disable-next-line no-restricted-syntax
    for await (const batch of batches) {
      const batchStartIndex = currentIndex;

      const pendingAPICalls = batch.map((order, index) => {
        const orderIndex = batchStartIndex + index;
        // Manejar errores individuales por orden
        return makeOrder(order)
          .then((result) => ({
            result,
            originalOrder: order,
            index: orderIndex,
            error: false
          }))
          .catch((error) => {
            if (process.env.NODE_ENV === 'development') {
              // eslint-disable-next-line no-console
              console.log('Error en orden individual:', error);
            }
            return {
              result: null,
              originalOrder: order,
              index: orderIndex,
              error: true,
              errorDetails: error
            };
          });
      });

      const batchResults = await Promise.all(pendingAPICalls);

      // Procesar cada resultado y notificar inmediatamente
      // eslint-disable-next-line no-restricted-syntax
      for (const { result, originalOrder, index, error } of batchResults) {
        let finalResult = null;

        if (!error && result) {
          const processedResult = processResponseData(result);
          finalResult = processedResult.length > 0 ? processedResult[0] : null;
        }

        results.push(finalResult);

        // Notificar actualización inmediata si hay callback
        if (onBatchComplete) {
          onBatchComplete({
            index,
            result: finalResult,
            originalOrder
          });
        }
      }

      currentIndex += batch.length;
      setCountPendingOrders((prev) => prev - batch.length);
    }

    return results;
  };

  return batchAPICalls;
};

export default useBatchAPICalls;
